import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import * as api from "./api";
import type { Me, PendingRequest, Tenant, User } from "./api";
import * as b2c from "./b2c";

const TENANT_KEY = "wise.tenant";

interface AuthState {
  user: User | null;
  tenants: Tenant[];
  pendingRequests: PendingRequest[];
  currentTenant: Tenant | null;
  loading: boolean;
  /** B2C isn't configured — sign-in is unavailable. */
  unavailable: boolean;
  /** Start sign-in; `email` is passed to the IdP as a login hint. */
  login: (email?: string) => void;
  logout: () => void;
  selectTenant: (slug: string) => void;
  /** Re-read /me (after creating a workspace or requesting access). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function resolveTenant(tenants: Tenant[], slug: string | null): Tenant | null {
  if (!tenants.length) return null;
  return tenants.find((t) => t.slug === slug) ?? tenants[0];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [pendingRequests, setPending] = useState<PendingRequest[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(() =>
    localStorage.getItem(TENANT_KEY),
  );
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  // Every API call gets a fresh (silently refreshed) B2C access token.
  useEffect(() => {
    api.setTokenProvider(b2c.getAccessToken);
    void b2c.loadConfig().then((c) => setUnavailable(!c.configured));
  }, []);

  const applyMe = useCallback((me: Me) => {
    setUser(me.user);
    setTenants(me.tenants);
    setPending(me.pending_requests ?? []);
    setSelectedSlug((prev) => {
      const t = resolveTenant(me.tenants, prev);
      if (t) localStorage.setItem(TENANT_KEY, t.slug);
      return t ? t.slug : null;
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!b2c.isSignedIn()) {
      setUser(null);
      setTenants([]);
      setPending([]);
      return;
    }
    try {
      applyMe(await api.fetchMe());
    } catch {
      b2c.clearSession();
      setUser(null);
      setTenants([]);
      setPending([]);
    }
  }, [applyMe]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const currentTenant = useMemo(
    () => resolveTenant(tenants, selectedSlug),
    [tenants, selectedSlug],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      tenants,
      pendingRequests,
      currentTenant,
      loading,
      unavailable,
      login(email) {
        void b2c.login(window.location.pathname, email);
      },
      logout() {
        localStorage.removeItem(TENANT_KEY);
        setUser(null);
        setTenants([]);
        setPending([]);
        void b2c.logout();
      },
      selectTenant(slug) {
        setSelectedSlug(slug);
        localStorage.setItem(TENANT_KEY, slug);
      },
      refresh,
    }),
    [user, tenants, pendingRequests, currentTenant, loading, unavailable, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
